/**
 * Skin analysis API client.
 * Centralizes detect-skin and condition-descriptions requests.
 */

import { getApiBaseUrl, SALON_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import { fetchJsonWithRetry } from './fetchWithRetry';
import { compressDetectImages } from './imageCompress';
import { TIMEOUT_MS, RETRY } from './salonResilience';

const DETECT_SKIN_PATH = `${SALON_API_PREFIX}/detect-skin`;
const DESCRIPTIONS_PATH = `${SALON_API_PREFIX}/generate-condition-descriptions`;
const STORED_DESCRIPTIONS_PATH = `${SALON_API_PREFIX}/get-stored-condition-descriptions`;
const GENERATE_MASK_PATH = `${SALON_API_PREFIX}/generate-skin-condition-mask`;

const DETECT_PROGRESS_STEP_MS = 2600;

const SINGLE_DETECT_PROGRESS_STEPS = [
  { phase: 'upload', message: 'Uploading your photo securely…' },
  { phase: 'scan', message: 'Scanning facial features…' },
  { phase: 'detect', message: 'Detecting skin conditions…' },
  { phase: 'score', message: 'Calculating your skin score…' },
  { phase: 'finalize', message: 'Preparing your results…' },
];

const BATCH_DETECT_PROGRESS_STEPS = [
  { phase: 'upload', message: 'Uploading your captures…' },
  { phase: 'scan', message: 'Processing front and side views…' },
  { phase: 'detect', message: 'Detecting conditions across all views…' },
  { phase: 'score', message: 'Aggregating your skin score…' },
  { phase: 'finalize', message: 'Preparing your personalized report…' },
];

function startDetectProgressTicker(onProgress, { batchSize, isBatch }) {
  if (typeof onProgress !== 'function') {
    return { stop() {}, pause() {}, resume() {} };
  }

  const steps = isBatch ? BATCH_DETECT_PROGRESS_STEPS : SINGLE_DETECT_PROGRESS_STEPS;
  let stepIndex = 0;
  let paused = false;
  let resumeTimerId = null;

  const emitStep = () => {
    if (paused) return;
    const step = steps[stepIndex];
    onProgress({
      phase: step.phase,
      message: step.message,
      batchSize,
      viewsCompleted: 0,
      stepIndex,
      stepCount: steps.length,
    });
  };

  emitStep();

  const intervalId = setInterval(() => {
    if (paused || stepIndex >= steps.length - 1) return;
    stepIndex += 1;
    emitStep();
  }, DETECT_PROGRESS_STEP_MS);

  return {
    stop: () => {
      if (resumeTimerId) clearTimeout(resumeTimerId);
      clearInterval(intervalId);
    },
    pause: () => {
      paused = true;
    },
    resume: () => {
      paused = false;
    },
    resumeAfter: (delayMs) => {
      if (resumeTimerId) clearTimeout(resumeTimerId);
      resumeTimerId = setTimeout(() => {
        resumeTimerId = null;
        paused = false;
      }, delayMs);
    },
  };
}

/**
 * @returns {string} Normalized API base URL (no trailing slash).
 */
function getBaseUrl() {
  const raw = getApiBaseUrl();
  return (raw || '').replace(/\/$/, '');
}

/**
 * Build full URL for an API path.
 * @param {string} path - e.g. '/api/salon/detect-skin'
 * @returns {string}
 */
export function buildSkinAnalysisApiUrl(path) {
  const base = getBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/**
 * Run batched skin detection (front + optional side captures in one request).
 */
export async function detectSkinBatch({
  frontImage,
  sideCaptures,
  sessionId,
  age,
  allowedConditions,
  appSessionToken,
  useOllamaForDescriptions,
  masksPolicy,
  retryCount = 0,
  onProgress,
  signal,
  onRetry,
  requestId,
}) {
  const front = frontImage || null;
  if (!front) {
    throw new Error('Front image is required');
  }

  const compressed = await compressDetectImages(front, sideCaptures);
  const side_captures = {};
  if (compressed.sideCaptures?.left) side_captures.left = compressed.sideCaptures.left;
  if (compressed.sideCaptures?.right) side_captures.right = compressed.sideCaptures.right;
  const sideCount = Object.keys(side_captures).length;
  const batchSize = 1 + sideCount;
  const progressTicker = startDetectProgressTicker(onProgress, {
    batchSize,
    isBatch: sideCount > 0,
  });

  const handleRetry = (info) => {
    progressTicker.pause();
    if (typeof onRetry === 'function') {
      onRetry(info);
    }
    progressTicker.resumeAfter(Number(info?.delayMs) || 0);
  };

  const url = buildSkinAnalysisApiUrl(DETECT_SKIN_PATH);
  const body = {
    image: compressed.frontImage,
    front_image: compressed.frontImage,
    conf_threshold: 0.06,
    session_id: sessionId ?? null,
    age: age ?? null,
    batch: sideCount > 0,
    retry_count: retryCount,
    ...(sideCount > 0 && { side_captures }),
    ...(side_captures.left && { left_image: side_captures.left }),
    ...(side_captures.right && { right_image: side_captures.right }),
    ...(allowedConditions && allowedConditions.length > 0 && { allowed_conditions: allowedConditions }),
    ...(appSessionToken && { app_session_token: appSessionToken }),
    ...(typeof useOllamaForDescriptions === 'boolean' && { use_ollama_for_descriptions: useOllamaForDescriptions }),
    ...(masksPolicy && { masks_policy: masksPolicy }),
  };

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (requestId && String(requestId).trim()) {
      headers['X-Request-ID'] = String(requestId).trim();
    }
    const data = await fetchJsonWithRetry(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        timeoutMs: sideCount > 0 ? TIMEOUT_MS.BATCH_DETECT : TIMEOUT_MS.SINGLE_DETECT,
        signal,
      },
      { label: 'salon-detect-skin-batch', onRetry: handleRetry, conflictMaxAttempts: RETRY.CONFLICT_MAX_ATTEMPTS }
    );

    if (typeof onProgress === 'function') {
      onProgress({
        phase: 'complete',
        message: 'Analysis complete',
        batchSize,
        viewsCompleted: batchSize,
      });
    }

    return data;
  } finally {
    progressTicker.stop();
  }
}

/** @deprecated Use detectSkinBatch */
export async function detectSkin(params) {
  return detectSkinBatch({
    frontImage: params.image,
    sessionId: params.sessionId,
    age: params.age,
    allowedConditions: params.allowedConditions,
    appSessionToken: params.appSessionToken,
    useOllamaForDescriptions: params.useOllamaForDescriptions,
    masksPolicy: params.masksPolicy,
  });
}

/** @deprecated Use detectSkinBatch */
export async function detectSkinWithSideCaptures(params) {
  return detectSkinBatch(params);
}

/**
 * Fetch AI-generated condition descriptions (Ollama).
 */
export async function fetchConditionDescriptions(payload) {
  if (!payload) return {};
  const url = buildSkinAnalysisApiUrl(DESCRIPTIONS_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 60000,
  });
  const data = await response.json().catch(() => ({}));
  if (!data.success || !data.descriptions || typeof data.descriptions !== 'object') {
    return {};
  }
  return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
}

/**
 * Fetch stored AI condition descriptions (fast; no Ollama call).
 */
export async function fetchStoredConditionDescriptions({
  sessionId,
  appSessionToken,
  generateIfMissing = false,
  requestedConditions,
  previousSkinData,
}) {
  if (!sessionId) return {};
  if (previousSkinData) {
    const url = buildSkinAnalysisApiUrl(STORED_DESCRIPTIONS_PATH);
    const body = {
      session_id: sessionId,
      ...(appSessionToken && { app_session_token: appSessionToken }),
      generate_if_missing: generateIfMissing,
      ...(Array.isArray(requestedConditions) && requestedConditions.length > 0
        ? { requested_conditions: requestedConditions }
        : {}),
      previous_skin_data: previousSkinData,
    };
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success || !data.descriptions || typeof data.descriptions !== 'object') {
      return {};
    }
    return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
  }

  const url = new URL(buildSkinAnalysisApiUrl(STORED_DESCRIPTIONS_PATH));
  url.searchParams.set('session_id', sessionId);
  if (appSessionToken) url.searchParams.set('app_session_token', appSessionToken);
  url.searchParams.set('generate_if_missing', generateIfMissing ? 'true' : 'false');
  if (Array.isArray(requestedConditions) && requestedConditions.length > 0) {
    url.searchParams.set('requested_conditions', requestedConditions.join(','));
  }
  const response = await fetchWithTimeout(url.toString(), { timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success || !data.descriptions || typeof data.descriptions !== 'object') {
    return {};
  }
  return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
}

/**
 * Lazily generate a single skin concern mask for a session.
 */
export async function generateSkinConditionMask({ sessionId, condition, appSessionToken }) {
  const url = buildSkinAnalysisApiUrl(GENERATE_MASK_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      condition,
      ...(appSessionToken && { app_session_token: appSessionToken }),
    }),
    timeoutMs: TIMEOUT_MS.SINGLE_DETECT,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || response.statusText || 'Mask request failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}
